/**
 * SalesConfigTab Component
 * Configure sales techniques for the AI agent
 */

import { useState, useEffect, useCallback } from 'react';
import {
  IconTargetArrow,
  IconTrendingUp,
  IconGauge,
  IconSettings,
} from '@tabler/icons-react';
import { useAgentsFlow } from '../../contexts/AgentsFlowContext';
import { SALES_PRIMARY_TECHNIQUES, SALES_STRATEGIES } from '../../constants';
import './SalesConfigTab.css';

// ===============================================
// DEFAULT SALES CONFIG
// ===============================================

const DEFAULT_CONFIG = {
  enabled: false,
  primary_technique: 'consultative',
  strategies: [],
  intensity: 3,
  max_attempts: 2,
  respect_no: true,
};

// ===============================================
// INTENSITY LABELS
// ===============================================

const INTENSITY_LABELS = {
  1: 'Muito sutil - só oferece se o cliente perguntar',
  2: 'Sutil - sugere quando há abertura clara',
  3: 'Moderada - oferece sugestões naturalmente',
  4: 'Proativo - busca oportunidades de venda',
  5: 'Muito proativo - apresenta ofertas ativamente',
};

// ===============================================
// MAIN COMPONENT
// ===============================================

const SalesConfigTab = () => {
  const { state, updateSalesConfig } = useAgentsFlow();
  const { editor } = state;

  const [config, setConfig] = useState(DEFAULT_CONFIG);

  useEffect(() => {
    if (editor?.sales_config) {
      setConfig({
        ...DEFAULT_CONFIG,
        ...editor.sales_config,
      });
    }
  }, [editor?.sales_config]);

  const handleChange = useCallback((key, value) => {
    const newConfig = { ...config, [key]: value };
    setConfig(newConfig);
    queueMicrotask(() => updateSalesConfig(newConfig));
  }, [config, updateSalesConfig]);

  const handleToggleStrategy = useCallback((strategyId) => {
    const strategies = config.strategies || [];
    const newStrategies = strategies.includes(strategyId)
      ? strategies.filter((s) => s !== strategyId)
      : [...strategies, strategyId];
    handleChange('strategies', newStrategies);
  }, [config.strategies, handleChange]);

  const activeStrategiesCount = (config.strategies || []).length;

  return (
    <div className="sales-config">
      {/* Header */}
      <section className="hum-section">
        <div className="hum-header">
          <div className="hum-header__left">
            <div className={`hum-header__icon ${config.enabled ? 'hum-header__icon--active' : ''}`}>
              <IconTargetArrow size={24} />
            </div>
            <div className="hum-header__info">
              <h3 className="hum-header__title">Técnicas de Vendas</h3>
              <span className="hum-header__desc">
                Configure como o agente aborda vendas e sugestões de produtos
              </span>
            </div>
          </div>
          <div className="hum-header__right">
            <span className="hum-header__status">
              {config.enabled ? 'Ativado' : 'Desativado'}
            </span>
            <label className="hum-toggle">
              <input
                type="checkbox"
                checked={config.enabled}
                onChange={(e) => handleChange('enabled', e.target.checked)}
              />
              <span className="hum-toggle__slider" />
            </label>
          </div>
        </div>
      </section>

      {config.enabled && (
        <>
          {/* Primary Technique */}
          <section className="hum-section">
            <div className="hum-section__header">
              <IconTargetArrow size={18} />
              <h3 className="hum-section__title">Técnica Principal</h3>
            </div>
            <p className="hum-section__desc">
              Escolha a abordagem de vendas que o agente deve seguir durante as conversas.
            </p>
            <div className="sales-techniques">
              {SALES_PRIMARY_TECHNIQUES.map((technique) => (
                <div
                  key={technique.id}
                  className={`sales-technique ${config.primary_technique === technique.id ? 'sales-technique--selected' : ''}`}
                  onClick={() => handleChange('primary_technique', technique.id)}
                >
                  <div className="sales-technique__radio" />
                  <div className="sales-technique__info">
                    <span className="sales-technique__name">{technique.name}</span>
                    <span className="sales-technique__desc">{technique.description}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Complementary Strategies */}
          <section className="hum-section">
            <div className="hum-section__header">
              <IconTrendingUp size={18} />
              <h3 className="hum-section__title">Estratégias Complementares</h3>
            </div>
            <p className="hum-section__desc">
              Selecione estratégias adicionais que o agente pode usar.
              {activeStrategiesCount > 0 && (
                <span className="hum-counter ms-2">
                  {activeStrategiesCount} ativa{activeStrategiesCount !== 1 ? 's' : ''}
                </span>
              )}
            </p>
            <div className="sales-strategies">
              {SALES_STRATEGIES.map((strategy) => {
                const isSelected = (config.strategies || []).includes(strategy.id);
                return (
                  <div
                    key={strategy.id}
                    className={`sales-strategy ${isSelected ? 'sales-strategy--selected' : ''}`}
                    onClick={() => handleToggleStrategy(strategy.id)}
                  >
                    <div className="sales-strategy__check" />
                    <div className="sales-strategy__info">
                      <span className="sales-strategy__name">{strategy.name}</span>
                      <span className="sales-strategy__desc">{strategy.description}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Intensity */}
          <section className="hum-section">
            <div className="hum-section__header">
              <IconGauge size={18} />
              <h3 className="hum-section__title">Intensidade</h3>
            </div>
            <p className="hum-section__desc">
              Defina o quão proativo o agente deve ser ao oferecer produtos e sugestões.
            </p>
            <div className="sales-intensity">
              <div className="sales-intensity__slider">
                <input
                  type="range"
                  className="sales-intensity__input"
                  min={1}
                  max={5}
                  value={config.intensity}
                  onChange={(e) => handleChange('intensity', parseInt(e.target.value))}
                />
                <span className="sales-intensity__value">{config.intensity}</span>
              </div>
              <span className="sales-intensity__label">
                {INTENSITY_LABELS[config.intensity]}
              </span>
            </div>
          </section>

          {/* Settings */}
          <section className="hum-section">
            <div className="hum-section__header">
              <IconSettings size={18} />
              <h3 className="hum-section__title">Configurações</h3>
            </div>
            <div className="sales-settings">
              <div className="sales-setting">
                <div className="sales-setting__info">
                  <span className="sales-setting__name">Máximo de tentativas</span>
                  <span className="sales-setting__desc">
                    Número máximo de sugestões de upsell/cross-sell por conversa
                  </span>
                </div>
                <div className="sales-setting__control">
                  <input
                    type="number"
                    className="sales-setting__number"
                    min={1}
                    max={5}
                    value={config.max_attempts}
                    onChange={(e) => {
                      const val = Math.min(5, Math.max(1, parseInt(e.target.value) || 1));
                      handleChange('max_attempts', val);
                    }}
                  />
                </div>
              </div>

              <div className="sales-setting">
                <div className="sales-setting__info">
                  <span className="sales-setting__name">Respeitar recusa</span>
                  <span className="sales-setting__desc">
                    Parar de insistir em uma sugestão após o cliente recusar
                  </span>
                </div>
                <div className="sales-setting__control">
                  <label className="hum-toggle">
                    <input
                      type="checkbox"
                      checked={config.respect_no}
                      onChange={(e) => handleChange('respect_no', e.target.checked)}
                    />
                    <span className="hum-toggle__slider" />
                  </label>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {!config.enabled && (
        <div className="hum-empty">
          <IconTargetArrow size={48} />
          <h3 className="hum-empty__title">Técnicas de Vendas desativadas</h3>
          <span className="hum-empty__desc">
            Ative as técnicas de vendas para que o agente utilize estratégias como SPIN Selling, Upsell e Cross-sell durante as conversas.
          </span>
        </div>
      )}
    </div>
  );
};

export default SalesConfigTab;
